import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import {
  getLscCommissionerDigest,
  listLscVodCandidates,
  LscApiError,
} from '../api';
import type { LscCommissionerDigest, LscVodCandidate } from '../types';
import VodSyncPanel from '../components/VodSyncPanel';
import VodCandidatesTable from '../components/VodCandidatesTable';
import VodCandidatesFilters from '../components/VodCandidatesFilters';
import VodSyncActivityBanner from '../components/VodSyncActivityBanner';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import PaginatedListFooter from '../components/PaginatedListFooter';
import CommissionerOnlyGate from '../components/CommissionerOnlyGate';
import { lscReturnTo } from '../routing';
import { usePaginatedList } from '../hooks/usePaginatedList';
import {
  filterVodCandidatesByAvailability,
  countUnavailableVodCandidates,
  AVAILABILITY_FILTER_LABELS,
  type AvailabilityFilterMode,
} from '../vodAvailability';
import {
  filterVodInventoryCandidates,
  parseVodInventoryFilterParam,
  sortVodInventoryCandidatesBySyncTouch,
  summarizeVodInventoryFromCandidates,
  VOD_INVENTORY_EMPTY_MESSAGES,
  VOD_INVENTORY_FILTER_ORDER,
  VOD_INVENTORY_NOT_VERIFIED_DISCLAIMER,
  vodInventoryFilterUsesSyncSort,
  type VodInventoryFilter,
} from '../vodTruth';
import { formatVodSyncLastRunLabel, lastSuccessfulVodSyncCompletedAt } from '../vodSyncLastRun';

const LscVodSyncPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/vods`)}>
      <CommissionerOnlyGate>
        <VodsContent />
      </CommissionerOnlyGate>
    </LscAuthGate>
  );
};

function parseAvailabilityFilterParam(value: string | null): AvailabilityFilterMode {
  if (value === 'unavailable' || value === 'all') {
    return value;
  }
  return 'available';
}

function VodsContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidates, setCandidates] = useState<LscVodCandidate[]>([]);
  const [digest, setDigest] = useState<LscCommissionerDigest | null>(null);
  const [digestError, setDigestError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilterMode>(() =>
    parseAvailabilityFilterParam(searchParams.get('availability'))
  );

  const selectedFilter = useMemo(
    () =>
      parseVodInventoryFilterParam(
        searchParams.get('filter'),
        searchParams.get('match_status')
      ),
    [searchParams]
  );

  const loadCandidates = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listLscVodCandidates(leagueId, { limit: 500 });
      setCandidates(data);
    } catch (err) {
      if (err instanceof LscApiError && err.status === 403) {
        setError('Only league commissioners can sync and review VOD candidates right now.');
      } else {
        setError(err instanceof LscApiError ? err.message : 'Could not load VOD candidates.');
      }
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const loadDigest = useCallback(async () => {
    if (!leagueId) return;
    setDigestError(null);
    try {
      const data = await getLscCommissionerDigest(leagueId, {
        window_hours: 168,
        sample_limit: 5,
      });
      setDigest(data);
    } catch {
      setDigest(null);
      setDigestError('Sync summary unavailable. VOD candidates are still available below.');
    }
  }, [leagueId]);

  useEffect(() => {
    setAvailabilityFilter(parseAvailabilityFilterParam(searchParams.get('availability')));
  }, [searchParams]);

  useEffect(() => {
    void loadCandidates();
    void loadDigest();
  }, [loadCandidates, loadDigest]);

  const fallbackSummary = useMemo(
    () => summarizeVodInventoryFromCandidates(candidates),
    [candidates]
  );

  const filterCounts = useMemo(() => {
    const counts: Partial<Record<VodInventoryFilter, number>> = {
      all: candidates.length,
    };
    for (const filter of VOD_INVENTORY_FILTER_ORDER) {
      if (filter === 'all') {
        continue;
      }
      counts[filter] = filterVodInventoryCandidates(candidates, filter).length;
    }
    return counts;
  }, [candidates]);

  const unavailableCount = useMemo(
    () => countUnavailableVodCandidates(candidates),
    [candidates]
  );

  const displayedCandidates = useMemo(() => {
    let rows = filterVodInventoryCandidates(candidates, selectedFilter);
    rows = filterVodCandidatesByAvailability(rows, availabilityFilter);
    if (vodInventoryFilterUsesSyncSort(selectedFilter)) {
      rows = sortVodInventoryCandidatesBySyncTouch(rows);
    }
    return rows;
  }, [candidates, selectedFilter, availabilityFilter]);

  const pagination = usePaginatedList(displayedCandidates);

  const emptyMessage = useMemo(() => {
    if (candidates.length === 0) {
      return undefined;
    }
    if (selectedFilter === 'all') {
      return undefined;
    }
    return VOD_INVENTORY_EMPTY_MESSAGES[selectedFilter];
  }, [candidates.length, selectedFilter]);

  const handleFilterSelect = (filter: VodInventoryFilter) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (filter === 'all') {
        next.delete('filter');
      } else {
        next.set('filter', filter);
      }
      next.delete('match_status');
      return next;
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">VOD candidates</h1>
        <p className="text-sm text-secondary-400">
          Sync and review Twitch VOD metadata matched to league games.{' '}
          {VOD_INVENTORY_NOT_VERIFIED_DISCLAIMER}
        </p>
        <p className="text-sm text-secondary-300 mt-2" data-testid="vod-sync-last-run">
          {formatVodSyncLastRunLabel(lastSuccessfulVodSyncCompletedAt(digest))}
        </p>
        <p className="text-xs text-secondary-500 mt-2">
          Some Twitch VODs expire. LSC helps identify unavailable videos so they do not clutter
          review.
          {unavailableCount > 0
            ? ` ${unavailableCount} unavailable Twitch VOD${unavailableCount === 1 ? '' : 's'} hidden by default.`
            : ''}
        </p>
      </div>

      {leagueId && (
        <VodSyncPanel
          leagueId={leagueId}
          onSynced={() => {
            void loadCandidates();
            void loadDigest();
          }}
        />
      )}

      {!loading && leagueId ? (
        <VodSyncActivityBanner
          leagueId={leagueId}
          digest={digest}
          digestError={digestError}
          fallbackSummary={fallbackSummary}
        />
      ) : null}

      {error && <ErrorBanner message={error} />}

      {!loading ? (
        <div className="flex flex-wrap items-end gap-3">
          <VodCandidatesFilters
            selected={selectedFilter}
            counts={filterCounts}
            onSelect={handleFilterSelect}
          />
          <label className="flex flex-col gap-1 text-sm ml-auto">
            <span className="text-secondary-400">Availability</span>
            <select
              className="input-field"
              value={availabilityFilter}
              onChange={(event) => {
                const next = event.target.value as AvailabilityFilterMode;
                setAvailabilityFilter(next);
                pagination.reset();
                setSearchParams((current) => {
                  const params = new URLSearchParams(current);
                  if (next === 'available') {
                    params.delete('availability');
                  } else {
                    params.set('availability', next);
                  }
                  return params;
                });
              }}
              data-testid="vod-availability-filter"
            >
              <option value="available">{AVAILABILITY_FILTER_LABELS.available}</option>
              <option value="all">{AVAILABILITY_FILTER_LABELS.all}</option>
              <option value="unavailable">{AVAILABILITY_FILTER_LABELS.unavailable}</option>
            </select>
          </label>
        </div>
      ) : null}

      {loading ? (
        <LoadingState message="Loading VOD candidates..." />
      ) : (
        leagueId && (
          <>
            <VodCandidatesTable
              leagueId={leagueId}
              candidates={pagination.visibleItems}
              emptyMessage={emptyMessage}
            />
            <PaginatedListFooter
              visibleCount={pagination.visibleCount}
              totalCount={pagination.totalCount}
              hasMore={pagination.hasMore}
              onShowMore={pagination.showMore}
              itemLabel="VODs"
            />
          </>
        )
      )}
    </div>
  );
}

export default LscVodSyncPage;
